import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { DogListItem } from "@/components/DogListItem";
import { makeDogStateKey } from "@/lib/dogIdentity";

export function DogList({
  dogs,
  selectedDayKey,
  completedDogs,
  urgentDogs,
  daysList,
  onDogClick,
  onUrgentClick,
  onDelete,
  onEdit,
  onReorder,
  currentDate,
  getInitialDayData,
  requireConfirmation,
  onApplyCanceledDaysBatch,
  isReadOnly,
  reorderUnlocked
}) {
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  React.useEffect(() => {
    if (isReadOnly && pendingConfirmation) {
      setPendingConfirmation(null);
    }
  }, [isReadOnly, pendingConfirmation]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const getDogSortableId = (dog) => dog.id || dog.name;

  const handleDragEnd = ({ active, over }) => {
    if (isReadOnly || !reorderUnlocked || !over || active.id === over.id) return;
    setPendingConfirmation(null);
    if (typeof onReorder === "function") {
      const oldIndex = dogs.findIndex(d => getDogSortableId(d) === active.id);
      const newIndex = dogs.findIndex(d => getDogSortableId(d) === over.id);
      const newOrder = arrayMove(dogs, oldIndex, newIndex).map((dog, i) => ({
        ...dog,
        originalOrder: i
      }));
      onReorder(newOrder);
    }
  };

  const handleDogClickIntercepted = (dogName, dogId) => {
    if (isReadOnly) return;
    if (!requireConfirmation) {
      onDogClick(dogName, dogId);
      return;
    }

    if (pendingConfirmation) {
      setPendingConfirmation(null);
      return;
    }

    const dogKey = makeDogStateKey(selectedDayKey, { id: dogId, name: dogName });
    const isCurrentlyCompleted = !!(completedDogs?.[dogKey]);
    setPendingConfirmation({ dogName, dogId, type: 'marking', isCurrentlyCompleted });
  };

  const handleUrgentClickIntercepted = (dogName, dogId, dateKeyOverride) => {
    if (isReadOnly) return;
    const targetDateKey = dateKeyOverride !== undefined ? dateKeyOverride : selectedDayKey;
    
    if (!requireConfirmation) {
      onUrgentClick(dogName, dogId, targetDateKey);
      return;
    }

    if (pendingConfirmation) {
      setPendingConfirmation(null);
      return;
    }

    const dogKey = makeDogStateKey(targetDateKey, { id: dogId, name: dogName });
    const isCurrentlyUrgent = !!(urgentDogs?.[dogKey]);
    setPendingConfirmation({ dogName, dogId, type: 'urgent', isCurrentlyUrgent, dateKey: targetDateKey });
  };

  const handleConfirmAction = () => {
    if (isReadOnly) return;
    if (pendingConfirmation) {
      if (pendingConfirmation.type === 'urgent') {
        onUrgentClick(pendingConfirmation.dogName, pendingConfirmation.dogId);
      } else {
        onDogClick(pendingConfirmation.dogName, pendingConfirmation.dogId);
      }
      setPendingConfirmation(null);
    }
  };

  const handleCancelAction = () => {
    setPendingConfirmation(null);
  };

  if (!dogs || dogs.length === 0) {
    return <p className="text-center text-muted-foreground mt-8">No hay clientes para este día.</p>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={dogs.map(getDogSortableId)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className="space-y-0"
          onClick={() => { if (pendingConfirmation) setPendingConfirmation(null); }}
        >
           {dogs.map((dog, index) => (
             <DogListItem
               key={`${selectedDayKey}-${getDogSortableId(dog)}`}
               dog={dog}
               index={index}
               selectedDayKey={selectedDayKey}
               completedDogs={completedDogs}
               urgentDogs={urgentDogs}
               daysList={daysList}
               onDogClick={handleDogClickIntercepted}
               onUrgentClick={handleUrgentClickIntercepted}
               onDelete={onDelete}
               onEdit={onEdit}
               currentDate={currentDate}
               getInitialDayData={getInitialDayData}
               pendingConfirmation={pendingConfirmation}
               onConfirmAction={handleConfirmAction}
               onCancelAction={handleCancelAction}
               requireConfirmation={requireConfirmation}
               onApplyCanceledDaysBatch={onApplyCanceledDaysBatch}
               isReadOnly={isReadOnly}
             />
           ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
